import React from 'react';
import { useMemo, useEffect, useContext } from 'react';
import { createStore, type StoreApi, useStore } from 'zustand';

/**
 * ZustandStore is a special type of React Context where the value is wrapped into a zustand store.
 * This enables us the ease of avoiding prop drilling while also limiting re-renders to when the specific prop changes.
 * With regular Contexts, when any prop in the context changes, *all* components using the Context would re-render.
 * With ZustandStores, only components using the specific props will re-render.
 */

/** Create a memoized store for a context. Pass this to the Provider component of the associated Context.
 * Every prop should be memoized (if non-primitive) */
export function useContextStore<T extends object>(value: T): StoreApi<T> {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const store = useMemo(() => createStore(() => value), []);

  useEffect(() => {
    const differentKeys = Object.keys(value).filter(
      (key) => value[key as keyof T] !== store.getState()[key as keyof T],
    );
    if (differentKeys.length > 0) {
      store.setState(value);
    }
  }, [store, value]);

  return store;
}

/** Use a prop from a context store.
 * Efficiently subscribes only the prop used, not the entire store. */
export function useContextProp<T, K extends keyof T>(
  context: React.Context<StoreApi<T> | undefined>,
  key: K,
): T[K] {
  const store = useContext(context);
  if (!store)
    throw new Error(
      'useContextProp called without a store in context. Add a provider component.',
    );

  return useStore(store, (state) => state[key]);
}

/** Create a context for a zustand store */
export function createZustandContext<T>() {
  return React.createContext<StoreApi<T> | undefined>(undefined);
}
